import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Plus,
  Copy,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { PageTitle } from '../components/work/page-states';
import { PageFrame } from '../components/work/page-chrome';
import { useOverlays } from '../components/work/overlay-context';
import { useSession } from '../components/session-context';
import { apiFetch, v1Path } from '../lib/api';
import { formatTimestampDateTime } from '../lib/dates';
import { cn } from '../lib/utils';
import {
  deleteServiceRequestTemplate,
  deleteServiceRequestType,
  getServiceRequestRoles,
  listServiceRequestTemplates,
  saveServiceRequestTemplate,
  orderedSectionKeys,
  listServiceRequestConfigAudit,
  listServiceRequestTypes,
  putServiceRequestRoles,
  SUBMISSION_COMMON_FIELD_OPTIONS,
  serviceRequestFieldLabel,
  serviceRequestHandoffs,
  type ReadinessTemplateItem,
  type ServiceRequestConfigAuditRow,
  type ServiceRequestRolesResponse,
  type ServiceRequestTemplateDto,
  type ServiceRequestOptionColor,
  type ServiceRequestType,
} from '../lib/service-requests';
import { isMemberDto, memberDisplayName, parseItems, type MemberDto, type ProjectDto, type StateDto } from '../lib/work-types';
import { isWorkspaceProject } from '../lib/workspace-projects';

export type CustomFieldDraft = {
  key: string;
  label: string;
  /** `standard` places a built-in Service Request field (Customer, Sales owner…) in the section. */
  type: 'string' | 'text_long' | 'date' | 'choice' | 'choice_other' | 'multi_choice' | 'quantity_list' | 'boolean' | 'positive_integer' | 'confirmation' | 'standard';
  /** Choice list answers, comma-separated. */
  options: string;
  help: string;
  requiredAtSubmission: boolean;
  /** Choice list / Yes-No answer that opens a required details box; empty for none. */
  detailsWhen: string;
  /** Choice → red / amber / green, for answers that read as a status. */
  colors?: Record<string, ServiceRequestOptionColor>;
  /** Choice + quantity list: the Number question (key) that shows the total of its quantities. */
  totalInto?: string;
  /** Multiple choice that also accepts a typed answer. */
  allowTypedOther?: boolean;
};

type QuestionType = Exclude<CustomFieldDraft['type'], 'standard'>;

export type SectionDraft = {
  key: string;
  label: string;
  fields: CustomFieldDraft[];
};

export type RuleDraft = {
  id: string;
  when: 'submit_review' | 'deployment_approval';
  field: string;
  operator: 'equals' | 'not_equals' | 'is_answered' | 'is_not_answered';
  value: string;
  message: string;
};

export type HandoffDraft = {
  projectId: string;
  /** Column in that stream that means the department's part is done. */
  readyStateId: string;
  taskTemplates: TaskTemplateDraft[];
};

export type TaskTemplateDraft = {
  title: string;
  ownerRole: string;
  dueOffsetDays: string;
};

type Draft = {
  name: string;
  description: string;
  department: string;
  sla: string;
  initialRoles: string[];
  deploymentRoles: string[];
  requiredAttachments: string[];
  requiredAttachmentGuidance: Record<string, string>;
  submissionTypeDataFields: string[];
  submissionRequireAttachments: boolean;
  readiness: ReadinessTemplateItem[];
  validation: ReadinessTemplateItem[];
  sections: SectionDraft[];
  rules: RuleDraft[];
  linkStream: boolean;
  /** Departments the approved request is handed to, one task each. */
  handoffs: HandoffDraft[];
  /** Selectable by requestors: published and not disabled. */
  active: boolean;
};

const FIELD_TYPE_LABELS: Record<QuestionType, string> = {
  string: 'Short text',
  text_long: 'Long text',
  choice: 'Choice list',
  choice_other: 'Choice list + Other',
  multi_choice: 'Multiple choice',
  quantity_list: 'Choice + quantity list',
  boolean: 'Yes / No',
  positive_integer: 'Number',
  date: 'Date',
  confirmation: 'Sign-off',
};
const QUESTION_TYPES = Object.keys(FIELD_TYPE_LABELS) as QuestionType[];
const STANDARD_PREFIX = 'detail:';

const detailsKeyFor = (key: string) => `${key}_details`;

export function isChoiceType(type: CustomFieldDraft['type']): boolean {
  return type === 'choice' || type === 'choice_other' || type === 'multi_choice' || type === 'quantity_list';
}

export function choicesOf(field: Pick<CustomFieldDraft, 'options'>): string[] {
  return field.options.split(',').map((value) => value.trim()).filter(Boolean);
}

/** Answers a question can take, for the "ask for details when" setting. */
export function answersOf(field: Pick<CustomFieldDraft, 'type' | 'options'>): string[] {
  if (field.type === 'boolean') return ['Yes', 'No'];
  if (field.type === 'choice' || field.type === 'choice_other') return choicesOf(field);
  return [];
}

export function fieldKeyOf(field: CustomFieldDraft): string {
  if (field.type === 'standard') return field.key;
  const key = field.key || slugKey(field.label);
  return (SUBMISSION_COMMON_FIELD_OPTIONS as readonly string[]).includes(key) ? `q_${key}` : key;
}

function standardItem(key: string, required: boolean): CustomFieldDraft {
  return { key, label: serviceRequestFieldLabel(key), type: 'standard', options: '', help: '', requiredAtSubmission: required, detailsWhen: '' };
}

function sectionsFromType(type: ServiceRequestType): SectionDraft[] {
  const schema = type.field_schema ?? {};
  const conditional = schema.conditional_required ?? {};
  const mustConfirm = new Set(schema.must_confirm ?? []);
  const kinds = schema.question_kinds ?? {};
  const multi = new Set(schema.multi_select ?? []);
  const allowOther = new Set(schema.allow_other ?? []);
  const quantityLists = new Set(schema.quantity_list ?? []);
  const required = new Set(type.submission_requirements?.type_data_fields ?? []);
  const requiredStandard = new Set(type.submission_requirements?.fields ?? []);
  const placedStandard = new Set<string>();
  const detailsKeys = new Set(Object.entries(conditional)
    .filter(([key, condition]) => key === detailsKeyFor(condition.field))
    .map(([key]) => key));

  const questionFor = (fieldKey: string): CustomFieldDraft => {
    const kind = kinds[fieldKey];
    const stored = schema.options?.[fieldKey] ?? [];
    const condition = conditional[detailsKeyFor(fieldKey)];
    const detailsWhen = condition ? (condition.equals === true ? 'Yes' : String(condition.equals)) : '';
    // Earlier builds stored "Yes / No + details" and a TBC switch; both read as plain choices now.
    let type: QuestionType;
    if (mustConfirm.has(fieldKey) || kind === 'confirmation') type = 'confirmation';
    else if (stored.length && quantityLists.has(fieldKey)) type = 'quantity_list';
    else if (stored.length && multi.has(fieldKey)) type = 'multi_choice';
    else if (stored.length && allowOther.has(fieldKey)) type = 'choice_other';
    else if (stored.length) type = 'choice';
    else if (kind === 'yes_no_details' || schema.field_types?.[fieldKey] === 'boolean') type = 'boolean';
    else type = ((schema.field_types?.[fieldKey] as QuestionType | undefined) ?? 'string');
    return {
      key: fieldKey,
      label: schema.field_labels?.[fieldKey] ?? serviceRequestFieldLabel(fieldKey),
      type,
      options: isChoiceType(type) ? stored.join(', ') : '',
      help: schema.field_help?.[fieldKey] ?? '',
      requiredAtSubmission: type === 'confirmation' || required.has(fieldKey),
      detailsWhen,
      ...(schema.option_colors?.[fieldKey] ? { colors: schema.option_colors[fieldKey] } : {}),
      ...(type === 'quantity_list' && schema.quantity_total?.[fieldKey] ? { totalInto: schema.quantity_total[fieldKey] } : {}),
      ...(type === 'multi_choice' && allowOther.has(fieldKey) ? { allowTypedOther: true } : {}),
    };
  };

  const sections = orderedSectionKeys(schema)
    .map((key) => [key, schema.sections?.[key] ?? []] as const)
    .map(([key, keys]) => {
      const layout = schema.section_layout?.[key]
        ?? (Array.isArray(keys) ? keys : []);
      const fields = layout
        .filter((entry) => !detailsKeys.has(entry))
        .map((entry) => {
          if (entry.startsWith(STANDARD_PREFIX)) {
            const standardKey = entry.slice(STANDARD_PREFIX.length);
            placedStandard.add(standardKey);
            return standardItem(standardKey, requiredStandard.has(standardKey));
          }
          return questionFor(entry);
        });
      return { key, label: schema.section_labels?.[key] ?? serviceRequestFieldLabel(key), fields };
    });

  const unplaced = Array.from(requiredStandard).filter((key) => !placedStandard.has(key));
  if (unplaced.length) {
    sections.unshift({ key: 'request_details', label: 'Request details', fields: unplaced.map((key) => standardItem(key, true)) });
  }
  return sections;
}

function rulesFromType(type: ServiceRequestType): RuleDraft[] {
  return (type.rules ?? []).map((rule) => ({
    id: rule.id,
    when: rule.when,
    field: rule.field,
    operator: rule.operator,
    value: rule.value === null || rule.value === undefined ? '' : String(rule.value),
    message: rule.message,
  }));
}

/** Turns the editor's sections and questions back into the stored field schema. */
function configPatchFor(type: ServiceRequestType | null, draft: Draft) {
  const sections: Record<string, string[]> = {};
  const sectionLayout: Record<string, string[]> = {};
  const sectionLabels: Record<string, string> = {};
  const fieldTypes: Record<string, string> = {};
  const fieldLabels: Record<string, string> = {};
  const fieldHelp: Record<string, string> = {};
  const options: Record<string, string[]> = {};
  const conditional: Record<string, { field: string; equals: string | boolean }> = {};
  const questionKinds: Record<string, QuestionType> = {};
  const mustConfirm: string[] = [];
  const allowOther: string[] = [];
  const multiSelect: string[] = [];
  const quantityList: string[] = [];
  const quantityTotal: Record<string, string> = {};
  const optionColors: Record<string, Record<string, ServiceRequestOptionColor>> = {};
  const typeDataFields: string[] = [];
  const submissionFields: string[] = [];
  const usedSectionKeys = new Set<string>();

  draft.sections.forEach((section, index) => {
    let sectionKey = section.key || slugKey(section.label) || `section_${index + 1}`;
    if (usedSectionKeys.has(sectionKey)) sectionKey = `${sectionKey}_${index + 1}`;
    usedSectionKeys.add(sectionKey);
    sectionLabels[sectionKey] = section.label.trim() || `Section ${index + 1}`;
    const keys: string[] = [];
    const layout: string[] = [];
    for (const field of section.fields) {
      if (field.type === 'standard') {
        layout.push(`${STANDARD_PREFIX}${field.key}`);
        if (field.requiredAtSubmission) submissionFields.push(field.key);
        continue;
      }
      const key = fieldKeyOf(field);
      if (!key) continue;
      keys.push(key);
      layout.push(key);
      questionKinds[key] = field.type;
      fieldLabels[key] = field.label.trim();
      if (field.help.trim()) fieldHelp[key] = field.help.trim();
      if (field.type === 'boolean' || field.type === 'confirmation') {
        fieldTypes[key] = 'boolean';
      } else if (isChoiceType(field.type)) {
        // Multiple-choice and quantity-list answers are lists, so they carry no scalar field type.
        if (field.type === 'multi_choice') {
          multiSelect.push(key);
          if (field.allowTypedOther) allowOther.push(key);
        }
        else if (field.type === 'quantity_list') {
          quantityList.push(key);
          if (field.totalInto) quantityTotal[key] = field.totalInto;
        } else fieldTypes[key] = 'string';
        if (field.type === 'choice_other' || field.type === 'quantity_list') allowOther.push(key);
        const choices = Array.from(new Set(choicesOf(field)));
        if (choices.length) options[key] = choices;
        const colored = Object.fromEntries(Object.entries(field.colors ?? {}).filter(([choice]) => choices.includes(choice)));
        if (Object.keys(colored).length) optionColors[key] = colored;
      } else {
        fieldTypes[key] = field.type;
      }
      if (field.type === 'confirmation') {
        mustConfirm.push(key);
        typeDataFields.push(key);
        continue;
      }
      if (field.requiredAtSubmission) typeDataFields.push(key);
      if (field.detailsWhen && answersOf(field).includes(field.detailsWhen)) {
        const details = detailsKeyFor(key);
        keys.push(details);
        layout.push(details);
        fieldTypes[details] = 'text_long';
        fieldLabels[details] = `${field.label.trim()}: details`;
        conditional[details] = { field: key, equals: field.type === 'boolean' ? field.detailsWhen === 'Yes' : field.detailsWhen };
        typeDataFields.push(details);
      }
    }
    sections[sectionKey] = keys;
    sectionLayout[sectionKey] = layout;
  });
  const declaredKeys = new Set(Object.values(sections).flat());
  for (const [list, total] of Object.entries(quantityTotal)) {
    if (!declaredKeys.has(total) || fieldTypes[total] !== 'positive_integer') delete quantityTotal[list];
  }

  const handoffs = draft.handoffs
    .filter((handoff) => handoff.projectId)
    .map((handoff) => ({
      linked_project_id: handoff.projectId,
      ready_state_id: handoff.readyStateId || null,
      task_templates: handoff.taskTemplates
        .filter((template) => template.title.trim())
        .map((template) => ({
          title: template.title.trim(),
          owner_role: template.ownerRole.trim() || null,
          due_offset_days: template.dueOffsetDays.trim() ? Number(template.dueOffsetDays) : null,
        })),
    }));

  return {
    field_schema: {
      ...(type?.field_schema ?? {}),
      sections,
      section_labels: sectionLabels,
      options,
      field_types: fieldTypes,
      field_labels: fieldLabels,
      field_help: fieldHelp,
      section_layout: sectionLayout,
      section_order: Object.keys(sectionLayout),
      conditional_required: conditional,
      must_confirm: mustConfirm,
      allow_tbc: [],
      allow_other: allowOther,
      multi_select: multiSelect,
      quantity_list: quantityList,
      quantity_total: quantityTotal,
      option_colors: optionColors,
      question_kinds: questionKinds,
    },
    typeDataFields: Array.from(new Set(typeDataFields)),
    submissionFields: Array.from(new Set(submissionFields)),
    rules: draft.rules.map((rule) => ({
      id: rule.id,
      when: rule.when,
      field: rule.field,
      operator: rule.operator,
      value: rule.operator === 'equals' || rule.operator === 'not_equals' ? rule.value : null,
      message: rule.message.trim(),
    })),
    stage_config: {
      ...(type?.stage_config ?? {}),
      approved: {
        ...(type?.stage_config?.approved ?? {}),
        link_stream: draft.linkStream,
        handoffs: draft.linkStream ? handoffs : [],
        linked_project_id: null,
        ready_state_id: null,
        task_templates: [],
      },
    },
  };
}

function handoffsFromType(type: ServiceRequestType): HandoffDraft[] {
  return serviceRequestHandoffs(type).map((handoff) => ({
    projectId: handoff.linked_project_id,
    readyStateId: handoff.ready_state_id ?? '',
    taskTemplates: (handoff.task_templates ?? []).map((template) => ({
      title: template.title,
      ownerRole: template.owner_role ?? '',
      dueOffsetDays: template.due_offset_days === null || template.due_offset_days === undefined ? '' : String(template.due_offset_days),
    })),
  }));
}

function draftFor(type: ServiceRequestType): Draft {
  return {
    name: type.name,
    description: type.description,
    department: type.department ?? '',
    sla: type.sla_days == null ? '' : String(type.sla_days),
    initialRoles: [...type.initial_approval_roles],
    deploymentRoles: [...type.deployment_approval_roles],
    requiredAttachments: [...(type.required_attachments ?? [])],
    requiredAttachmentGuidance: { ...(type.required_attachment_guidance ?? {}) },
    submissionTypeDataFields: [...(type.submission_requirements?.type_data_fields ?? [])],
    submissionRequireAttachments: type.submission_requirements?.require_attachments ?? true,
    readiness: (type.readiness_template ?? []).map((item) => ({ key: item.key, label: item.label, required: item.required ?? false })),
    validation: (type.validation_template ?? []).map((item) => ({ key: item.key, label: item.label, required: item.required ?? false })),
    sections: sectionsFromType(type),
    rules: rulesFromType(type),
    linkStream: (type.stage_config?.approved ?? type.stage_config?.in_progress)?.link_stream === true,
    handoffs: handoffsFromType(type),
    active: type.status !== 'draft' && !type.archived_at,
  };
}

/**
 * A new-type draft built from a saved template. Handoffs to streams that no longer
 * exist are dropped; the result saves as an inactive draft until published.
 */
export function draftFromTemplate(template: ServiceRequestTemplateDto, projectIds: Set<string>): Draft {
  const config = template.config;
  const asType = {
    ...config,
    id: '',
    key: '',
    name: '',
    description: config.description ?? '',
    initial_approval_roles: config.initial_approval_roles ?? [],
    deployment_approval_roles: config.deployment_approval_roles ?? [],
    status: 'draft',
    archived_at: null,
  } as unknown as ServiceRequestType;
  const draft: Draft = JSON.parse(JSON.stringify(draftFor(asType)));
  const handoffs = draft.handoffs.filter((handoff) => !handoff.projectId || projectIds.has(handoff.projectId));
  return { ...draft, handoffs, linkStream: draft.linkStream && handoffs.length > 0, active: false };
}

/** Trimmed, de-duplicated role list, in selection order, as sent to the API. */
export function normalizeRoles(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

const ROLE_MIN_ERROR = 'Select at least one role.';

type DraftErrorKey = 'name' | 'initialRoles' | 'deploymentRoles' | 'readiness' | 'validation' | 'requiredAttachments' | 'customFields' | 'rules';

/** Field-level problems that keep Save disabled (contract §7 validation mirrored client-side). */
export function typeDraftErrors(draft: Pick<Draft, 'name' | 'initialRoles' | 'deploymentRoles' | 'readiness' | 'requiredAttachments'> & {
  validation?: ReadinessTemplateItem[];
  sections?: SectionDraft[];
  rules?: RuleDraft[];
}): Partial<Record<DraftErrorKey, string>> {
  const errors: Partial<Record<DraftErrorKey, string>> = {};
  if (!draft.name.trim()) errors.name = 'Name is required.';
  if (normalizeRoles(draft.initialRoles).length === 0) errors.initialRoles = ROLE_MIN_ERROR;
  if (normalizeRoles(draft.deploymentRoles).length === 0) errors.deploymentRoles = ROLE_MIN_ERROR;
  const labels = draft.readiness.map((item) => item.label.trim().toLocaleLowerCase());
  if (draft.readiness.some((item) => !item.label.trim())) {
    errors.readiness = 'Every readiness check needs a name.';
  } else if (new Set(labels).size !== labels.length) {
    errors.readiness = 'Readiness check names must be unique.';
  }
  const validation = draft.validation ?? [];
  const validationLabels = validation.map((item) => item.label.trim().toLocaleLowerCase());
  if (validation.some((item) => !item.label.trim())) {
    errors.validation = 'Every validation check needs a name.';
  } else if (new Set(validationLabels).size !== validationLabels.length) {
    errors.validation = 'Validation check names must be unique.';
  }
  const documentNames = draft.requiredAttachments.map((label) => label.trim().toLocaleLowerCase());
  if (draft.requiredAttachments.some((label) => !label.trim())) {
    errors.requiredAttachments = 'Document names cannot be empty.';
  } else if (new Set(documentNames).size !== documentNames.length) {
    errors.requiredAttachments = 'Document names must be unique.';
  }
  const sections = draft.sections ?? [];
  const questions = sections.flatMap((section) => section.fields).filter((field) => field.type !== 'standard');
  const fieldKeys = questions.flatMap((field) => {
    const key = fieldKeyOf(field);
    return field.detailsWhen ? [key, detailsKeyFor(key)] : [key];
  });
  if (sections.some((section) => !section.label.trim())) {
    errors.customFields = 'Every section needs a name.';
  } else if (questions.some((field) => !field.label.trim())) {
    errors.customFields = 'Every question needs a label.';
  } else if (questions.some((field) => !fieldKeyOf(field))) {
    errors.customFields = 'Questions must contain at least one letter or number.';
  } else if (new Set(fieldKeys).size !== fieldKeys.length) {
    errors.customFields = 'Two questions have the same wording.';
  } else if (questions.some((field) => isChoiceType(field.type) && choicesOf(field).length === 0)) {
    errors.customFields = 'Every choice list needs at least one choice.';
  }
  const rules = draft.rules ?? [];
  if (rules.some((rule) => !rule.field || !rule.message.trim())) {
    errors.rules = 'Every rule needs a field and a message.';
  } else if (rules.some((rule) => (rule.operator === 'equals' || rule.operator === 'not_equals') && !rule.value.trim())) {
    errors.rules = 'Rules that check a value need one.';
  }
  return errors;
}

function slugKey(label: string): string {
  return label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
}

const RULE_WHEN_LABELS: Record<RuleDraft['when'], string> = {
  submit_review: 'Submit for review',
  deployment_approval: 'Request deployment approval',
};
const RULE_OPERATOR_LABELS: Record<RuleDraft['operator'], string> = {
  equals: 'must equal',
  not_equals: 'must not equal',
  is_answered: 'must be answered',
  is_not_answered: 'must not be answered',
};

/** Simple `when / check / condition / block-with-message` rules (§5.4); no scripting UI. */
function RulesEditor({
  id,
  values,
  fieldOptions,
  onChange,
  error,
}: {
  id: string;
  values: RuleDraft[];
  fieldOptions: string[];
  onChange: (values: RuleDraft[]) => void;
  error?: string;
}) {
  function update(index: number, patch: Partial<RuleDraft>) {
    onChange(values.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  }
  function remove(index: number) {
    onChange(values.filter((_, position) => position !== index));
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>Rules</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onChange([...values, {
            id: `rule-${Date.now()}-${values.length}`, when: 'deployment_approval', field: fieldOptions[0] ?? '',
            operator: 'equals', value: '', message: '',
          }])}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add rule
        </Button>
      </div>
      {values.length === 0 ? <p className="text-xs text-muted-foreground">No rules yet. A rule can block a transition with a message, e.g. “Payment terms agreed? must equal Yes before deployment approval.”</p> : null}
      {values.map((rule, index) => (
        <div key={rule.id} className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">
            When <span className="font-medium text-foreground">{RULE_WHEN_LABELS[rule.when]}</span>, if{' '}
            <span className="font-medium text-foreground">{rule.field ? serviceRequestFieldLabel(rule.field) : '(field)'}</span>{' '}
            {RULE_OPERATOR_LABELS[rule.operator]}{(rule.operator === 'equals' || rule.operator === 'not_equals') ? ` "${rule.value || '…'}"` : ''}, block with the message below.
          </p>
          <div className="grid gap-2 sm:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor={`${id}-when-${index}`}>When</Label>
              <Select value={rule.when} onValueChange={(next) => update(index, { when: next as RuleDraft['when'] })}>
                <SelectTrigger id={`${id}-when-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(RULE_WHEN_LABELS) as RuleDraft['when'][]).map((when) => (
                    <SelectItem key={when} value={when}>{RULE_WHEN_LABELS[when]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${id}-field-${index}`}>Check field</Label>
              <Select value={rule.field || undefined} onValueChange={(next) => update(index, { field: next })}>
                <SelectTrigger id={`${id}-field-${index}`}><SelectValue placeholder="Select a field" /></SelectTrigger>
                <SelectContent>
                  {fieldOptions.map((field) => <SelectItem key={field} value={field}>{serviceRequestFieldLabel(field)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${id}-operator-${index}`}>Condition</Label>
              <Select value={rule.operator} onValueChange={(next) => update(index, { operator: next as RuleDraft['operator'] })}>
                <SelectTrigger id={`${id}-operator-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(RULE_OPERATOR_LABELS) as RuleDraft['operator'][]).map((operator) => (
                    <SelectItem key={operator} value={operator}>{RULE_OPERATOR_LABELS[operator]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(rule.operator === 'equals' || rule.operator === 'not_equals') ? (
              <div className="space-y-1">
                <Label htmlFor={`${id}-value-${index}`}>Value</Label>
                <Input id={`${id}-value-${index}`} value={rule.value} onChange={(event) => update(index, { value: event.target.value })} />
              </div>
            ) : null}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor={`${id}-message-${index}`}>Message shown to the user</Label>
              <Input id={`${id}-message-${index}`} value={rule.message} onChange={(event) => update(index, { message: event.target.value })} />
            </div>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label={`Remove rule ${index + 1}`} onClick={() => remove(index)}>
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      ))}
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * After initial approval, create one task on an existing stream. The rows below become its subtasks.
 */
const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function DepartmentEditor({
  id,
  index,
  handoff,
  projects,
  roles,
  onChange,
  onRemove,
}: {
  id: string;
  index: number;
  handoff: HandoffDraft;
  projects: ProjectDto[];
  roles: string[];
  onChange: (patch: Partial<HandoffDraft>) => void;
  onRemove: () => void;
}) {
  const [newTitle, setNewTitle] = useState('');
  const [columns, setColumns] = useState<StateDto[]>([]);
  const stream = projects.find((project) => project.id === handoff.projectId);
  const finish = columns.find((column) => column.id === handoff.readyStateId);
  const { taskTemplates } = handoff;

  useEffect(() => {
    if (!handoff.projectId) {
      setColumns([]);
      return;
    }
    let cancelled = false;
    void apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${handoff.projectId}/states`))
      .then((result) => {
        if (!cancelled) setColumns(result.items.filter((state) => state.group !== 'cancelled').sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
      })
      .catch(() => { if (!cancelled) setColumns([]); });
    return () => { cancelled = true; };
  }, [handoff.projectId]);

  function updateTemplate(position: number, patch: Partial<TaskTemplateDraft>) {
    onChange({ taskTemplates: taskTemplates.map((row, at) => (at === position ? { ...row, ...patch } : row)) });
  }
  function addTemplate() {
    const title = newTitle.trim();
    if (!title) return;
    onChange({ taskTemplates: [...taskTemplates, { title, ownerRole: '', dueOffsetDays: '' }] });
    setNewTitle('');
  }

  return (
    <div className="space-y-3 rounded-xl border border-input bg-card p-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full bg-amber-100 dark:bg-amber-950/60 text-xs font-semibold text-amber-800 dark:text-amber-200">{index + 1}</span>
        <div className="min-w-[12rem] flex-1 space-y-1">
          <Label htmlFor={`${id}-project`} className="text-xs text-muted-foreground">Stream</Label>
          <select
            id={`${id}-project`}
            className={cn(SELECT_CLASS, 'w-full bg-card')}
            value={handoff.projectId}
            onChange={(event) => onChange({ projectId: event.target.value, readyStateId: '' })}
          >
            <option value="">Choose a stream…</option>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </select>
        </div>
        <div className="min-w-[12rem] flex-1 space-y-1">
          <Label htmlFor={`${id}-finish`} className="text-xs text-muted-foreground">Done when the task reaches</Label>
          <select
            id={`${id}-finish`}
            className={cn(SELECT_CLASS, 'w-full bg-card')}
            value={handoff.readyStateId}
            disabled={!handoff.projectId}
            onChange={(event) => onChange({ readyStateId: event.target.value })}
          >
            <option value="">No finish column</option>
            {columns.map((column) => <option key={column.id} value={column.id}>{column.name}</option>)}
          </select>
        </div>
        <RemoveButton label={`Remove department ${index + 1}`} onClick={onRemove} />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground" aria-live="polite">
        <span className="rounded-full bg-blue-50 dark:bg-blue-950/40 px-2.5 py-1 text-blue-700 dark:text-blue-300">Approved</span>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="rounded-full bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 text-amber-700 dark:text-amber-300">
          Task in {stream?.name ?? '…'}{taskTemplates.length ? ` + ${taskTemplates.length} subtask${taskTemplates.length === 1 ? '' : 's'}` : ''}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 text-emerald-700 dark:text-emerald-300">{finish ? `Done at ${finish.name}` : 'No finish column'}</span>
      </div>

      {taskTemplates.length ? (
        <ul className="divide-y divide-border rounded-lg border border-input">
          {taskTemplates.map((template, position) => (
            <li key={position} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <Input
                value={template.title}
                aria-label={`Subtask ${position + 1} for department ${index + 1}`}
                className="h-8 min-w-[12rem] flex-1"
                onChange={(event) => updateTemplate(position, { title: event.target.value })}
              />
              <select
                aria-label={`Owner for subtask ${position + 1} for department ${index + 1}`}
                className={cn(SELECT_CLASS, 'h-8')}
                value={template.ownerRole}
                onChange={(event) => updateTemplate(position, { ownerRole: event.target.value })}
              >
                <option value="">No owner</option>
                {template.ownerRole && !roles.includes(template.ownerRole) ? <option value={template.ownerRole}>{template.ownerRole}</option> : null}
                {roles.map((role) => <option key={role} value={role}>{role}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Due in
                <Input
                  type="number"
                  min="0"
                  aria-label={`Days until subtask ${position + 1} is due`}
                  className="h-8 w-16"
                  value={template.dueOffsetDays}
                  onChange={(event) => updateTemplate(position, { dueOffsetDays: event.target.value })}
                />
                days
              </label>
              <RemoveButton label={`Remove subtask ${position + 1}`} onClick={() => onChange({ taskTemplates: taskTemplates.filter((_, at) => at !== position) })} />
            </li>
          ))}
        </ul>
      ) : null}
      <AddRow
        value={newTitle}
        onValueChange={setNewTitle}
        onAdd={addTemplate}
        placeholder="Add a subtask (optional)"
        inputLabel={`New subtask for department ${index + 1}`}
        buttonLabel="Add"
      />
    </div>
  );
}

const EMPTY_HANDOFF: HandoffDraft = { projectId: '', readyStateId: '', taskTemplates: [] };

function DepartmentsEditor({
  id,
  handoffs,
  projects,
  roles,
  onChange,
}: {
  id: string;
  handoffs: HandoffDraft[];
  projects: ProjectDto[];
  roles: string[];
  onChange: (handoffs: HandoffDraft[]) => void;
}) {
  return (
    <div className="space-y-3">
      {handoffs.map((handoff, index) => {
        const taken = new Set(handoffs.filter((_, at) => at !== index).map((row) => row.projectId));
        return (
          <DepartmentEditor
            key={index}
            id={`${id}-${index}`}
            index={index}
            handoff={handoff}
            projects={projects.filter((project) => !taken.has(project.id))}
            roles={roles}
            onChange={(patch) => onChange(handoffs.map((row, at) => (at === index ? { ...row, ...patch } : row)))}
            onRemove={() => onChange(handoffs.filter((_, at) => at !== index))}
          />
        );
      })}
      <Button type="button" variant="outline" size="sm" disabled={handoffs.length >= 10} onClick={() => onChange([...handoffs, { ...EMPTY_HANDOFF }])}>
        <Plus className="h-4 w-4" aria-hidden /> Add department
      </Button>
      {handoffs.length > 1 ? (
        <p className="text-xs text-muted-foreground">The request moves to Ready for deployment once every department with a finish column has reached it.</p>
      ) : null}
    </div>
  );
}

/**
 * Approval roles are matched by exact name against the org's role → stream mapping, so they are
 * picked rather than typed. New roles can still be introduced; map them to a stream once saved.
 */
function RolePicker({
  id,
  label,
  description,
  options,
  counts,
  selected,
  onChange,
  onAddRole,
  error,
  hideHeader = false,
}: {
  id: string;
  label: string;
  description: string;
  options: string[];
  counts: Record<string, number>;
  selected: string[];
  onChange: (roles: string[]) => void;
  onAddRole: (role: string) => void;
  error?: string;
  hideHeader?: boolean;
}) {
  const [newRole, setNewRole] = useState('');
  const chosen = new Set(selected);
  const unassigned = selected.filter((role) => (counts[role] ?? 0) === 0);

  function moveRole(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  function addRole() {
    const clean = newRole.trim();
    if (!clean) return;
    onAddRole(clean);
    if (!chosen.has(clean)) onChange([...selected, clean]);
    setNewRole('');
  }

  return (
    <div className="space-y-2">
      {hideHeader ? null : (
        <>
          <div className="flex items-center justify-between gap-2">
            <Label>{label}</Label>
            <Badge variant="secondary">{selected.length}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">{description}</p>
        </>
      )}
      {selected.length > 0 ? (
        <ol className="flex flex-wrap items-center gap-1 text-xs" aria-label={`${label} ordered approval chain`}>
          {selected.map((role, index) => (
            <li key={role} className="flex items-center gap-1">
              {index > 0 ? <span className="text-muted-foreground" aria-hidden>→</span> : null}
              <Badge variant="outline">{index + 1}. {role}</Badge>
              <span className="inline-flex">
                <button type="button" className="rounded p-0.5 text-muted-foreground enabled:hover:bg-accent disabled:opacity-30" disabled={index === 0} aria-label={`Move ${role} earlier`} onClick={() => moveRole(index, -1)}>
                  <ArrowLeft className="h-3 w-3" aria-hidden />
                </button>
                <button type="button" className="rounded p-0.5 text-muted-foreground enabled:hover:bg-accent disabled:opacity-30" disabled={index === selected.length - 1} aria-label={`Move ${role} later`} onClick={() => moveRole(index, 1)}>
                  <ArrowRight className="h-3 w-3" aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      <div className="divide-y divide-border rounded-lg border border-input bg-card">
        {options.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground">No roles yet</p> : null}
        {options.map((role) => {
          const inputId = `${id}-${slugKey(role)}`;
          const holders = counts[role] ?? 0;
          const position = selected.indexOf(role);
          return (
            <label key={role} htmlFor={inputId} className={cn('flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50', position >= 0 && 'bg-violet-50/60 dark:bg-violet-950/30')}>
              <Checkbox
                id={inputId}
                aria-label={role}
                checked={chosen.has(role)}
                onCheckedChange={(checked) => onChange(checked === true
                  ? [...selected, role]
                  : selected.filter((value) => value !== role))}
              />
              <span className="min-w-0 flex-1 truncate">{role}</span>
              <span className={cn('shrink-0 text-xs', holders === 0 ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}>
                {holders === 0 ? 'No one assigned' : `${holders} ${holders === 1 ? 'person' : 'people'}`}
              </span>
            </label>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={newRole}
          aria-label={`Add a new role for ${label.toLowerCase()}`}
          placeholder="Add a role"
          className="h-8 bg-card"
          onChange={(event) => setNewRole(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            addRole();
          }}
        />
        <Button type="button" size="sm" variant="outline" disabled={!newRole.trim()} onClick={addRole}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add
        </Button>
      </div>
      {unassigned.length > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Nobody can approve {unassigned.join(', ')} yet. Name approvers under Approval roles.
        </p>
      ) : null}
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function auditEntityLabel(row: ServiceRequestConfigAuditRow): string {
  if (row.entity === 'type') return `Type · ${row.entity_name ?? row.entity_id ?? '—'}`;
  if (row.entity === 'roles') return `Roles · ${row.entity_name ?? row.field ?? '—'}`;
  if (row.entity === 'settings') return 'Settings';
  if (row.entity === 'template') return `Template · ${row.entity_name ?? '—'}`;
  return row.entity_name ?? row.entity;
}

function auditValue(value: string | null): string {
  if (value == null || value === '') return '—';
  return value.length > 120 ? `${value.slice(0, 117)}…` : value;
}

function ConfigChangeLog() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ServiceRequestConfigAuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || rows !== null) return;
    let cancelled = false;
    listServiceRequestConfigAudit(100)
      .then((items) => {
        if (!cancelled) setRows(items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load configuration change log');
      });
    return () => { cancelled = true; };
  }, [open, rows]);

  return (
    <Card>
      <CardHeader>
        <button type="button" className="flex w-full items-center gap-2 text-left" aria-expanded={open} aria-controls="sr-config-audit" onClick={() => setOpen((value) => !value)}>
          {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
          <CardTitle className="text-base">Configuration change log</CardTitle>
        </button>
        <CardDescription>Who changed catalog types, approval roles and settings, and when. Latest 100 entries.</CardDescription>
      </CardHeader>
      {open ? (
        <CardContent id="sr-config-audit">
          {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
          {!error && rows === null ? <p className="text-sm text-muted-foreground">Loading change log…</p> : null}
          {rows !== null && rows.length === 0 ? <p className="text-sm text-muted-foreground">No configuration changes recorded yet.</p> : null}
          {rows !== null && rows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">When</TableHead>
                  <TableHead>What</TableHead>
                  <TableHead>Field</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="w-40">By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs text-muted-foreground">{formatTimestampDateTime(row.created_at) ?? row.created_at}</TableCell>
                    <TableCell className="text-sm">{auditEntityLabel(row)}</TableCell>
                    <TableCell className="text-sm">{row.field ? serviceRequestFieldLabel(row.field) : '—'}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs text-muted-foreground" title={row.old_value ?? undefined}>{auditValue(row.old_value)}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs" title={row.new_value ?? undefined}>{auditValue(row.new_value)}</TableCell>
                    <TableCell className="text-sm">{row.actor?.name ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}

/** Known roles first (server order), then any extra role an assignment, type or draft references. */
export function roleUniverse(...groups: string[][]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const group of groups) {
    for (const role of group) {
      const clean = role.trim();
      if (!clean || seen.has(clean)) continue;
      seen.add(clean);
      out.push(clean);
    }
  }
  return out;
}

const sameIds = (left: string[] = [], right: string[] = []) =>
  left.length === right.length && [...left].sort().join(',') === [...right].sort().join(',');

/** Chips for the chosen people plus a searchable "Add person" list. */
function ApproverPicker({
  id,
  role,
  members,
  selected,
  onChange,
}: {
  id: string;
  role: string;
  members: MemberDto[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const byId = new Map(members.map((member) => [member.user_id, member]));
  const available = members
    .filter((member) => !selected.includes(member.user_id))
    .sort((left, right) => memberDisplayName(left).localeCompare(memberDisplayName(right)));
  return (
    <div className="space-y-2">
      {selected.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={`${role} approvers`}>
          {selected.map((userId) => {
            const member = byId.get(userId);
            const name = member ? memberDisplayName(member) : 'Former member';
            return (
              <li key={userId} className="inline-flex items-center gap-1 rounded-full border bg-muted/60 py-0.5 pl-2.5 pr-1 text-xs">
                {name}
                <button
                  type="button"
                  aria-label={`Remove ${name} from ${role}`}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-accent hover:text-destructive"
                  onClick={() => onChange(selected.filter((entry) => entry !== userId))}
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button id={id} type="button" size="sm" variant="outline" disabled={available.length === 0}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> Add approver
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <Command>
            <CommandInput placeholder="Search people" />
            <CommandList>
              <CommandEmpty>No one found.</CommandEmpty>
              <CommandGroup>
                {available.map((member) => (
                  <CommandItem
                    key={member.user_id}
                    value={`${memberDisplayName(member)} ${member.email ?? ''}`}
                    onSelect={() => {
                      onChange([...selected, member.user_id]);
                      setOpen(false);
                    }}
                  >
                    <span className="truncate">{memberDisplayName(member)}</span>
                    {member.email ? <span className="ml-auto truncate pl-2 text-xs text-muted-foreground">{member.email}</span> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ApprovalRoles({
  allRoles,
  rolesInUse,
  data,
  onSaved,
  onAddRole,
  onRemoveRole,
}: {
  allRoles: string[];
  /** Roles a request type currently requires (saved or drafted); these can't be removed here. */
  rolesInUse: Set<string>;
  data: ServiceRequestRolesResponse | null;
  onSaved: (next: ServiceRequestRolesResponse) => void;
  onAddRole: (role: string) => void;
  onRemoveRole: (role: string) => void;
}) {
  const { toast } = useOverlays();
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [approvers, setApprovers] = useState<Record<string, string[]>>({});
  const [loaded, setLoaded] = useState<Record<string, string[]> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newRole, setNewRole] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const memberRes = await apiFetch<unknown>(v1Path('/members?limit=100&offset=0'));
        if (cancelled) return;
        setMembers(parseItems(memberRes, isMemberDto, 'organization members').filter((member) => member.is_active));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load organization members');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const apply = useCallback((next: ServiceRequestRolesResponse) => {
    const rows = Object.fromEntries(next.items.map((row) => [row.role, row.approver_ids]));
    setApprovers(rows);
    setLoaded(rows);
  }, []);

  /** A catalog save refetches the mapping; adopting it would drop edits the admin has not saved yet. */
  const pending = useRef(false);
  useEffect(() => {
    if (!data || pending.current) return;
    apply(data);
  }, [apply, data]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    const roles = normalizeRoles([...allRoles, ...Object.keys(loaded)]);
    return roles.some((role) => !sameIds(loaded[role], allRoles.includes(role) ? approvers[role] : []));
  }, [allRoles, approvers, loaded]);
  useEffect(() => { pending.current = dirty; }, [dirty]);

  function addRole() {
    const clean = newRole.trim();
    if (!clean) return;
    onAddRole(clean);
    setNewRole('');
  }

  async function save() {
    setSaving(true);
    try {
      const items = allRoles
        .filter((role) => (approvers[role] ?? []).length > 0)
        .map((role) => ({ role, user_ids: approvers[role] }));
      const result = await putServiceRequestRoles(items);
      apply(result);
      onSaved(result);
      toast('Approvers saved');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save approvers');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Who can approve</CardTitle>
        <CardDescription>
          Name the people who approve for each role. Any one of them can approve, and only they are emailed and see the
          request under Needs your approval. They don&apos;t need to be an admin of anything.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
        {!error && loaded === null ? <p className="text-sm text-muted-foreground">Loading roles…</p> : null}
        {loaded !== null ? (
          <>
            <div className="flex items-center gap-2">
              <Input
                value={newRole}
                aria-label="New approval role name"
                placeholder="New role name, e.g. Legal"
                className="max-w-xs"
                onChange={(event) => setNewRole(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  addRole();
                }}
              />
              <Button type="button" size="sm" variant="outline" disabled={!newRole.trim()} onClick={addRole}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> Add role
              </Button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {allRoles.map((role) => {
                const chosen = approvers[role] ?? [];
                const inUse = rolesInUse.has(role);
                const removable = chosen.length === 0 && !inUse;
                const pickerId = `approvers-${role.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
                return (
                  <fieldset key={role} className={cn('rounded-md border p-3', chosen.length === 0 && 'border-amber-300 dark:border-amber-900')}>
                    <legend className="flex w-full items-center gap-1 px-1 text-sm font-medium">
                      <span className="min-w-0 flex-1 truncate">{role}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${role} role`}
                        disabled={!removable}
                        title={inUse ? 'Used by a request type — remove it from that type first.' : !removable ? 'Remove its approvers before removing this role.' : 'Remove this role'}
                        className="rounded p-0.5 text-muted-foreground enabled:hover:bg-accent enabled:hover:text-destructive disabled:opacity-30"
                        onClick={() => onRemoveRole(role)}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </legend>
                    <ApproverPicker
                      id={pickerId}
                      role={role}
                      members={members}
                      selected={chosen}
                      onChange={(ids) => setApprovers((current) => ({ ...current, [role]: ids }))}
                    />
                    {chosen.length === 0 ? (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Nobody named — requests needing this role can&apos;t be submitted.
                      </p>
                    ) : null}
                  </fieldset>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2">
              {dirty ? <span className="mr-auto text-xs text-muted-foreground">Unsaved changes</span> : null}
              <Button variant="outline" disabled={saving || !dirty} onClick={() => setApprovers(loaded)}>Reset</Button>
              <Button disabled={saving || !dirty} onClick={() => void save()}>{saving ? 'Saving…' : 'Save approvers'}</Button>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

const BLANK_TYPE_DRAFT: Draft = {
  name: '',
  description: '',
  department: '',
  sla: '',
  initialRoles: [],
  deploymentRoles: [],
  requiredAttachments: [],
  requiredAttachmentGuidance: {},
  submissionTypeDataFields: [],
  submissionRequireAttachments: true,
  readiness: [],
  validation: [],
  sections: [],
  rules: [],
  linkStream: false,
  handoffs: [],
  active: false,
};

type EditorErrorKey = DraftErrorKey | 'department';

/** Approvers and a department are only required once the type is switched on for requestors. */
export function editorErrors(draft: Draft): Partial<Record<EditorErrorKey, string>> {
  const errors: Partial<Record<EditorErrorKey, string>> = { ...typeDraftErrors(draft) };
  if (!draft.active) {
    delete errors.initialRoles;
    delete errors.deploymentRoles;
  } else if (!draft.department.trim()) {
    errors.department = 'Add a department before making this type active.';
  }
  return errors;
}

function EditorPanel({
  step,
  title,
  description,
  aside,
  children,
}: {
  step: number;
  title: string;
  description: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-input bg-card shadow-sm">
      <header className="flex items-start gap-3 border-b border-border bg-muted/50 px-4 py-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background">{step}</span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold leading-tight text-foreground">{title}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        </div>
        {aside ? <div className="shrink-0 self-center">{aside}</div> : null}
      </header>
      <div className="space-y-3 p-4">{children}</div>
    </section>
  );
}

function Tally({ count, noun }: { count: number; noun: string }) {
  return (
    <span className={cn('text-xs font-medium', count ? 'text-foreground' : 'text-muted-foreground')}>
      {count} {noun}{count === 1 ? '' : 's'}
    </span>
  );
}

/** Accessible on/off switch; labelled by a <label htmlFor> or aria-label. */
function Toggle({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
    >
      <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5' : 'translate-x-0.5')} />
    </button>
  );
}

function CountPill({ count }: { count: number }) {
  return <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{count}</span>;
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label={label} onClick={onClick}>
      <Trash2 className="h-4 w-4" aria-hidden />
    </Button>
  );
}

function AddRow({
  value,
  onValueChange,
  onAdd,
  placeholder,
  inputLabel,
  buttonLabel,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  onAdd: () => void;
  placeholder: string;
  inputLabel: string;
  buttonLabel: string;
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-wrap gap-2">
      <Input
        ref={inputRef}
        value={value}
        aria-label={inputLabel}
        placeholder={placeholder}
        className="min-w-[14rem] flex-1 bg-card"
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          onAdd();
        }}
      />
      {children}
      <Button
        type="button"
        variant="outline"
        className="border-primary/50 text-primary hover:bg-primary/5"
        onClick={() => (value.trim() ? onAdd() : inputRef.current?.focus())}
      >
        <Plus className="h-4 w-4" aria-hidden /> {buttonLabel}
      </Button>
    </div>
  );
}

const CHECKBOX_PREFIX = /^[\s☐☑✓•*-]+/;

/**
 * Turns one pasted line into a question. "Deal status – Verbally agreed / Contract sent"
 * becomes a choice list; "… – Yes / No" becomes Yes / No.
 */
export function questionFromLine(line: string): CustomFieldDraft | null {
  const clean = line.replace(CHECKBOX_PREFIX, '').trim();
  if (!clean) return null;
  const match = clean.match(/^(.*?)\s+[–—-]\s+(.+\/.+)$/);
  const base = { key: '', help: '', requiredAtSubmission: true, detailsWhen: '' };
  if (!match) return { ...base, label: clean, type: 'string', options: '' };
  const choices = match[2].split('/').map((value) => value.trim()).filter(Boolean);
  if (choices.length === 2 && choices.every((value) => /^(yes|no)$/i.test(value))) {
    return { ...base, label: match[1].trim(), type: 'boolean', options: '' };
  }
  return { ...base, label: match[1].trim(), type: 'choice', options: choices.join(', ') };
}

// Beats the editor-wide input border so rows read as plain text until hovered or focused.
const BORDER_ON_HOVER = '!border-transparent hover:!border-input focus-visible:!border-input';
const PULSE_FIELD_PREFIX = 'pulse:';
const QUESTION_GRID = 'grid grid-cols-[minmax(0,1fr)_8.5rem_5.5rem_6.5rem_2rem] items-center gap-2 px-3 py-1';

const COLOR_CYCLE: Array<ServiceRequestOptionColor | null> = [null, 'green', 'amber', 'red'];
const CHOICE_DOT: Record<ServiceRequestOptionColor, string> = { red: 'bg-red-500', amber: 'bg-amber-500', green: 'bg-emerald-500' };

/** Choice list answers as removable chips, with a box to add more (Enter, or paste a list). */
function ChoiceChips({ id, field, onChange }: { id: string; field: CustomFieldDraft; onChange: (patch: Partial<CustomFieldDraft>) => void }) {
  const [draft, setDraft] = useState('');
  const choices = choicesOf(field);
  function add(values: string[]) {
    const next = Array.from(new Set([...choices, ...values.map((value) => value.trim()).filter(Boolean)]));
    onChange({ options: next.join(', ') });
    setDraft('');
  }
  function remove(choice: string) {
    const next = choices.filter((value) => value !== choice);
    onChange({ options: next.join(', '), ...(field.detailsWhen === choice ? { detailsWhen: '' } : {}) });
  }
  function cycleColor(choice: string) {
    const current = field.colors?.[choice];
    const next = COLOR_CYCLE[(COLOR_CYCLE.indexOf(current ?? null) + 1) % COLOR_CYCLE.length];
    const colors = { ...(field.colors ?? {}) };
    if (next) colors[choice] = next;
    else delete colors[choice];
    onChange({ colors });
  }
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">Choices</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {choices.map((choice) => (
          <span key={choice} className="inline-flex items-center gap-1 rounded-full bg-card py-0.5 pl-1.5 pr-1 text-xs text-foreground ring-1 ring-border">
            <button
              type="button"
              aria-label={`Colour for ${choice}: ${field.colors?.[choice] ?? 'none'}`}
              title="Colour: none, green, amber, red"
              className="rounded-full p-0.5 hover:bg-muted"
              onClick={() => cycleColor(choice)}
            >
              <span className={cn('block h-2.5 w-2.5 rounded-full', field.colors?.[choice] ? CHOICE_DOT[field.colors[choice]!] : 'border border-dashed border-muted-foreground/60')} />
            </button>
            {choice}
            <button type="button" aria-label={`Remove choice ${choice}`} className="rounded-full p-0.5 text-muted-foreground hover:bg-muted" onClick={() => remove(choice)}>
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          id={`${id}-add-choice`}
          value={draft}
          aria-label="Add a choice"
          placeholder={choices.length ? 'Add another…' : 'Type a choice and press Enter'}
          className="h-7 min-w-[10rem] flex-1 rounded-md border border-input bg-card px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onChange={(event) => setDraft(event.target.value)}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text');
            if (!/[,\n/]/.test(text)) return;
            event.preventDefault();
            add(text.split(/[,\n/]/));
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || !draft.trim()) return;
            event.preventDefault();
            add([draft]);
          }}
        />
      </div>
    </div>
  );
}

function QuestionRow({
  id,
  field,
  label,
  availableStandard,
  numberQuestions = [],
  autoFocus = false,
  onChange,
  onRemove,
}: {
  id: string;
  field: CustomFieldDraft;
  label: string;
  /** Pulse fields not yet placed anywhere on the form. */
  availableStandard: string[];
  /** Number questions in the same section, which a quantity list can total into. */
  numberQuestions?: CustomFieldDraft[];
  autoFocus?: boolean;
  onChange: (patch: Partial<CustomFieldDraft>) => void;
  onRemove: () => void;
}) {
  const answers = answersOf(field);
  const pulseFields = field.type === 'standard' ? [field.key, ...availableStandard] : availableStandard;
  const typeSelect = (
    <select
      aria-label={`How it's filled in, in ${label}`}
      className={cn(SELECT_CLASS, 'h-8 w-full bg-muted', BORDER_ON_HOVER)}
      value={field.type === 'standard' ? `${PULSE_FIELD_PREFIX}${field.key}` : field.type}
      onChange={(event) => {
        const value = event.target.value;
        if (value.startsWith(PULSE_FIELD_PREFIX)) {
          const key = value.slice(PULSE_FIELD_PREFIX.length);
          onChange({ ...standardItem(key, field.requiredAtSubmission) });
          setOpen(false);
          return;
        }
        const next = value as QuestionType;
        onChange({
          type: next,
          detailsWhen: '',
          ...(field.type === 'standard' ? { key: '', options: '' } : {}),
          ...(next === 'confirmation' ? { requiredAtSubmission: true } : {}),
        });
        if (isChoiceType(next) && !choicesOf(field).length) setOpen(true);
      }}
    >
      <optgroup label="Answer">
        {QUESTION_TYPES.map((type) => <option key={type} value={type}>{FIELD_TYPE_LABELS[type]}</option>)}
      </optgroup>
      {pulseFields.length ? (
        <optgroup label="Pulse field">
          {pulseFields.map((key) => <option key={key} value={`${PULSE_FIELD_PREFIX}${key}`}>{serviceRequestFieldLabel(key)}</option>)}
        </optgroup>
      ) : null}
    </select>
  );
  const choiceCount = choicesOf(field).length;
  const expandable = isChoiceType(field.type) || field.type === 'boolean';
  const [open, setOpen] = useState(isChoiceType(field.type) && choiceCount === 0);
  const summary = isChoiceType(field.type)
    ? `${choiceCount} choice${choiceCount === 1 ? '' : 's'}${field.detailsWhen ? ' · details' : ''}`
    : field.detailsWhen ? `Details on ${field.detailsWhen}` : null;

  if (field.type === 'standard') {
    return (
      <li className={QUESTION_GRID}>
        <span className="truncate px-2.5 text-sm">{field.label}</span>
        {typeSelect}
        <RequiredBox id={`${id}-required`} field={field} onChange={onChange} />
        <span aria-hidden />
        <RemoveButton label={`Remove ${field.label} in ${label}`} onClick={onRemove} />
      </li>
    );
  }

  return (
    <li>
      <div className={QUESTION_GRID}>
        <Input
          value={field.label}
          autoFocus={autoFocus}
          placeholder="Field name"
          aria-label={`Field in ${label}`}
          className={cn('h-8 min-w-0 bg-transparent shadow-none', BORDER_ON_HOVER)}
          onChange={(event) => onChange({ label: event.target.value })}
        />
        {typeSelect}
        {field.type === 'confirmation'
          ? <span className="text-xs text-muted-foreground">Must be ticked</span>
          : <RequiredBox id={`${id}-required`} field={field} onChange={onChange} />}
        {expandable ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} answer settings`}
            onClick={() => setOpen((value) => !value)}
            className="flex h-8 items-center justify-end gap-1 rounded-md px-1.5 text-xs text-muted-foreground hover:bg-muted"
          >
            {summary ? <span className="hidden sm:inline">{summary}</span> : null}
            <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />
          </button>
        ) : <span aria-hidden />}
        <RemoveButton label={`Remove ${field.label || 'question'} in ${label}`} onClick={onRemove} />
      </div>
      {expandable && open ? (
        <div className="mx-3 mb-2 space-y-3 rounded-lg bg-muted/50 px-3 py-2.5">
          {isChoiceType(field.type) ? <ChoiceChips id={id} field={field} onChange={onChange} /> : null}
          {field.type === 'choice_other' ? <p className="text-xs text-muted-foreground">Requestors also see “Other…” and can type their own answer.</p> : null}
          {field.type === 'multi_choice' ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Requestors can tick more than one choice.</p>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={field.allowTypedOther === true}
                  onCheckedChange={(checked) => onChange({ allowTypedOther: checked === true })}
                />
                Also let them type an answer that isn’t listed
              </label>
            </div>
          ) : null}
          {field.type === 'quantity_list' ? (
            <>
              <p className="text-xs text-muted-foreground">Requestors add a row per choice with a quantity, e.g. Blinx × 5, Europa × 3. “Other…” lets them type a choice that isn’t listed.</p>
              <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                Show the total in
                <select
                  aria-label="Show the total in"
                  className={cn(SELECT_CLASS, 'h-7 bg-card text-xs')}
                  value={field.totalInto ?? ''}
                  onChange={(event) => onChange({ totalInto: event.target.value || undefined })}
                >
                  <option value="">Don’t add up</option>
                  {numberQuestions.map((question) => (
                    <option key={fieldKeyOf(question)} value={fieldKeyOf(question)}>{question.label || 'Untitled number question'}</option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          {field.type !== 'multi_choice' && field.type !== 'quantity_list' ? <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            Ask for details when the answer is
            <select
              aria-label="Ask for details when the answer is"
              className={cn(SELECT_CLASS, 'h-7 bg-card text-xs')}
              value={field.detailsWhen}
              onChange={(event) => onChange({ detailsWhen: event.target.value })}
            >
              <option value="">Never</option>
              {answers.map((answer) => <option key={answer} value={answer}>{answer}</option>)}
            </select>
          </label> : null}
        </div>
      ) : null}
    </li>
  );
}

function RequiredBox({ id, field, onChange }: { id: string; field: CustomFieldDraft; onChange: (patch: Partial<CustomFieldDraft>) => void }) {
  return (
    <label htmlFor={id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Checkbox id={id} checked={field.requiredAtSubmission} onCheckedChange={(checked) => onChange({ requiredAtSubmission: checked === true })} />
      Required
    </label>
  );
}

function SectionEditor({
  id,
  section,
  index,
  availableStandard,
  autoFocus = false,
  onChange,
  onRemove,
}: {
  id: string;
  section: SectionDraft;
  index: number;
  availableStandard: string[];
  autoFocus?: boolean;
  onChange: (section: SectionDraft) => void;
  onRemove: () => void;
}) {
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const [focusAt, setFocusAt] = useState<number | null>(null);
  const togglable = section.fields.filter((field) => field.type !== 'confirmation');
  const allRequired = togglable.length > 0 && togglable.every((field) => field.requiredAtSubmission);
  const sectionName = section.label || `section ${index + 1}`;

  function addLines(lines: string[]) {
    const added = lines.map(questionFromLine).filter((field): field is CustomFieldDraft => field !== null);
    if (added.length) onChange({ ...section, fields: [...section.fields, ...added] });
  }
  function addQuestion() {
    setFocusAt(section.fields.length);
    onChange({
      ...section,
      fields: [...section.fields, { key: '', label: '', type: 'string', options: '', help: '', requiredAtSubmission: true, detailsWhen: '' }],
    });
  }
  function addPasted() {
    addLines(pasted.split(/\r?\n/).filter((line) => line.trim()));
    setPasted('');
    setPasting(false);
  }
  function updateField(position: number, patch: Partial<CustomFieldDraft>) {
    onChange({ ...section, fields: section.fields.map((row, at) => (at === position ? { ...row, ...patch } : row)) });
  }
  function setAllRequired(required: boolean) {
    onChange({ ...section, fields: section.fields.map((row) => (row.type === 'confirmation' ? row : { ...row, requiredAtSubmission: required })) });
  }

  return (
    <div className="overflow-hidden rounded-xl border border-input bg-card">
      <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-3 py-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">{index + 1}</span>
        <Input
          value={section.label}
          autoFocus={autoFocus}
          aria-label={`Section ${index + 1} name`}
          placeholder="Name this section, e.g. Customer & Sales Details"
          className={cn('h-8 flex-1 bg-transparent px-1 font-semibold shadow-none', BORDER_ON_HOVER)}
          onChange={(event) => onChange({ ...section, label: event.target.value })}
        />
        <label htmlFor={`${id}-all-required`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          All required
          <Toggle id={`${id}-all-required`} checked={allRequired} onChange={setAllRequired} />
        </label>
        <RemoveButton label={`Remove section ${index + 1}`} onClick={onRemove} />
      </div>
      {section.fields.length ? (
        <ul className="py-1">
          {section.fields.map((field, position) => (
            <QuestionRow
              key={position}
              id={`${id}-q${position}`}
              field={field}
              label={sectionName}
              availableStandard={availableStandard}
              numberQuestions={section.fields.filter((other) => other !== field && other.type === 'positive_integer')}
              autoFocus={focusAt === position}
              onChange={(patch) => updateField(position, patch)}
              onRemove={() => onChange({ ...section, fields: section.fields.filter((_, at) => at !== position) })}
            />
          ))}
        </ul>
      ) : null}
      {pasting ? (
        <div className="space-y-2 border-t border-border px-3 py-2.5">
          <textarea
            autoFocus
            value={pasted}
            aria-label={`Paste fields into section ${index + 1}`}
            placeholder={'One question per line, e.g.\nCompany type – University / Airport / Gym\nPlanogram discussed – Yes / No'}
            className="min-h-[6rem] w-full rounded-md border bg-card px-2.5 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onChange={(event) => setPasted(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={() => { setPasting(false); setPasted(''); }}>Cancel</Button>
            <Button type="button" size="sm" disabled={!pasted.trim()} onClick={addPasted}>Add fields</Button>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-primary/50 text-primary hover:bg-primary/5"
          onClick={addQuestion}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add field
        </Button>
        {!pasting ? (
          <Button type="button" size="sm" variant="ghost" className="ml-auto text-muted-foreground" onClick={() => setPasting(true)}>
            Paste a list
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The requestor's tick list: titled sections, each with questions and built-in
 * Pulse fields. Everything required blocks submission until answered.
 */
function QuestionsEditor({
  id,
  draft,
  onChange,
  error,
}: {
  id: string;
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  error?: string;
}) {
  const [focusSection, setFocusSection] = useState<number | null>(null);
  const placed = new Set(draft.sections.flatMap((section) => section.fields.filter((field) => field.type === 'standard').map((field) => field.key)));
  const availableStandard = SUBMISSION_COMMON_FIELD_OPTIONS.filter((field) => !placed.has(field));
  const items = draft.sections.flatMap((section) => section.fields).filter((field) => field.type !== 'confirmation');
  const allRequired = items.length > 0 && items.every((field) => field.requiredAtSubmission);

  function addSection() {
    setFocusSection(draft.sections.length);
    onChange({ sections: [...draft.sections, { key: '', label: '', fields: [] }] });
  }
  function setAllRequired(required: boolean) {
    onChange({
      sections: draft.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => (field.type === 'confirmation' ? field : { ...field, requiredAtSubmission: required })),
      })),
    });
  }

  return (
    <div className="space-y-3">
      {items.length ? (
        <div className="flex items-center justify-end">
          <label htmlFor={`${id}-all-required`} className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            Everything required
            <Toggle id={`${id}-all-required`} checked={allRequired} onChange={setAllRequired} />
          </label>
        </div>
      ) : null}

      {draft.sections.map((section, index) => (
        <SectionEditor
          key={index}
          id={`${id}-section-${index}`}
          section={section}
          index={index}
          availableStandard={availableStandard}
          autoFocus={focusSection === index}
          onChange={(next) => onChange({ sections: draft.sections.map((row, at) => (at === index ? next : row)) })}
          onRemove={() => onChange({ sections: draft.sections.filter((_, at) => at !== index) })}
        />
      ))}

      <button
        type="button"
        onClick={addSection}
        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-card py-3 text-sm font-medium text-primary transition hover:border-primary hover:bg-primary/5"
      >
        <Plus className="h-4 w-4" aria-hidden /> Add section
      </button>

      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function InlineChecklist({
  id,
  items,
  onChange,
  itemLabel,
  placeholder,
  error,
}: {
  id: string;
  items: ReadinessTemplateItem[];
  onChange: (items: ReadinessTemplateItem[]) => void;
  itemLabel: string;
  placeholder: string;
  error?: string;
}) {
  const [value, setValue] = useState('');
  function add() {
    const clean = value.trim();
    if (!clean) return;
    onChange([...items, { key: slugKey(clean), label: clean, required: true }]);
    setValue('');
  }
  return (
    <div className="space-y-2">
      {items.length === 0 ? null : (
        <ul className="divide-y divide-border rounded-lg border border-input bg-card">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2 px-3 py-1.5">
              <Input
                value={item.label}
                aria-label={`${itemLabel} ${index + 1}`}
                className="h-8 flex-1"
                onChange={(event) => onChange(items.map((row, position) => (
                  position === index ? { ...row, label: event.target.value, key: slugKey(event.target.value) } : row
                )))}
              />
              <label htmlFor={`${id}-required-${index}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox
                  id={`${id}-required-${index}`}
                  checked={item.required === true}
                  onCheckedChange={(checked) => onChange(items.map((row, position) => (position === index ? { ...row, required: checked === true } : row)))}
                />
                Required
              </label>
              <RemoveButton label={`Remove ${itemLabel.toLowerCase()} ${index + 1}`} onClick={() => onChange(items.filter((_, position) => position !== index))} />
            </li>
          ))}
        </ul>
      )}
      <AddRow
        value={value}
        onValueChange={setValue}
        onAdd={add}
        placeholder={placeholder}
        inputLabel={`New ${itemLabel.toLowerCase()}`}
        buttonLabel="Add"
      />
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function DocumentsEditor({
  values,
  guidance,
  onChange,
  error,
}: {
  values: string[];
  guidance: Record<string, string>;
  onChange: (patch: Pick<Draft, 'requiredAttachments' | 'requiredAttachmentGuidance'>) => void;
  error?: string;
}) {
  const [value, setValue] = useState('');
  function add() {
    const clean = value.trim();
    if (!clean) return;
    onChange({ requiredAttachments: [...values, clean], requiredAttachmentGuidance: guidance });
    setValue('');
  }
  function rename(index: number, nextName: string) {
    const previous = values[index] ?? '';
    const nextGuidance = { ...guidance };
    if (guidance[previous] !== undefined) {
      nextGuidance[nextName] = guidance[previous];
      delete nextGuidance[previous];
    }
    onChange({ requiredAttachments: values.map((row, position) => (position === index ? nextName : row)), requiredAttachmentGuidance: nextGuidance });
  }
  function remove(index: number) {
    const nextGuidance = { ...guidance };
    delete nextGuidance[values[index] ?? ''];
    onChange({ requiredAttachments: values.filter((_, position) => position !== index), requiredAttachmentGuidance: nextGuidance });
  }
  return (
    <div className="space-y-2">
      {values.length === 0 ? null : (
        <ul className="divide-y divide-border rounded-lg border border-input bg-card">
          {values.map((name, index) => (
            <li key={index} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
              <Input value={name} aria-label={`Required document ${index + 1}`} className="h-8 min-w-[10rem] flex-1" onChange={(event) => rename(index, event.target.value)} />
              <Input
                value={guidance[name] ?? ''}
                aria-label={`What document ${index + 1} must contain`}
                placeholder="Note (optional)"
                className="h-8 min-w-[12rem] flex-[2] text-xs"
                maxLength={500}
                onChange={(event) => onChange({ requiredAttachments: values, requiredAttachmentGuidance: { ...guidance, [name]: event.target.value } })}
              />
              <RemoveButton label={`Remove required document ${index + 1}`} onClick={() => remove(index)} />
            </li>
          ))}
        </ul>
      )}
      <AddRow
        value={value}
        onValueChange={setValue}
        onAdd={add}
        placeholder="e.g. Signed commercial agreement"
        inputLabel="New required document"
        buttonLabel="Add"
      />
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** One request type's full configuration, in the order a request lives through it. */
function TypeEditor({
  prefix,
  draft,
  errors,
  onChange,
  allRoles,
  roleCounts,
  onAddRole,
  projects,
}: {
  prefix: string;
  draft: Draft;
  errors: Partial<Record<EditorErrorKey, string>>;
  onChange: (patch: Partial<Draft>) => void;
  allRoles: string[];
  roleCounts: Record<string, number>;
  onAddRole: (role: string) => void;
  projects: ProjectDto[];
}) {
  const questionCount = draft.sections.reduce((sum, section) => sum + section.fields.length, 0);
  return (
    <div className="space-y-5 [&_input]:border-input [&_select]:border-input [&_textarea]:border-input">
      <EditorPanel
        step={1}
        title="Basics"
        description="Name the request type and say who owns it."
        aside={(
          <label htmlFor={`${prefix}-active`} className="flex items-center gap-2 text-sm font-medium text-foreground">
            {draft.active ? 'Available to requestors' : 'Hidden from requestors'}
            <Toggle id={`${prefix}-active`} checked={draft.active} onChange={(active) => onChange({ active })} />
          </label>
        )}
      >
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_8rem]">
          <div className="space-y-1">
            <Label htmlFor={`${prefix}-name`}>Name</Label>
            <Input
              id={`${prefix}-name`}
              value={draft.name}
              placeholder="e.g. Device Deployment"
              aria-invalid={Boolean(errors.name)}
              onChange={(event) => onChange({ name: event.target.value })}
            />
            {errors.name ? <p role="alert" className="text-xs text-destructive">{errors.name}</p> : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${prefix}-department`}>Owning department</Label>
            <Input id={`${prefix}-department`} value={draft.department} placeholder="e.g. Operations" aria-invalid={Boolean(errors.department)} onChange={(event) => onChange({ department: event.target.value })} />
            {errors.department ? <p role="alert" className="text-xs text-destructive">{errors.department}</p> : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${prefix}-sla`}>SLA (days)</Label>
            <Input id={`${prefix}-sla`} type="number" min="1" value={draft.sla} placeholder="e.g. 14" onChange={(event) => onChange({ sla: event.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-3">
            <Label htmlFor={`${prefix}-description`}>Description</Label>
            <Input id={`${prefix}-description`} value={draft.description} placeholder="One line shown on the request type card" onChange={(event) => onChange({ description: event.target.value })} />
          </div>
        </div>
      </EditorPanel>

      <EditorPanel
        step={2}
        title="Prerequisites"
        description="Build the form the requestor fills in. Add a section, then its questions. A request can't be submitted until every required item is answered."
        aside={<Tally count={questionCount} noun="item" />}
      >
        <QuestionsEditor id={`${prefix}-questions`} draft={draft} onChange={onChange} error={errors.customFields} />
      </EditorPanel>

      <EditorPanel
        step={3}
        title="Documents"
        description="Files the requestor must attach before submitting. Leave empty if none are needed."
        aside={<Tally count={draft.requiredAttachments.length} noun="document" />}
      >
        <DocumentsEditor values={draft.requiredAttachments} guidance={draft.requiredAttachmentGuidance} onChange={onChange} error={errors.requiredAttachments} />
      </EditorPanel>

      <div className="grid gap-5 md:grid-cols-2">
        <EditorPanel
          step={4}
          title="Initial approval"
          description="Tick who must approve before work starts. They approve in the order you tick them."
          aside={<Tally count={draft.initialRoles.length} noun="approver" />}
        >
          <RolePicker
            id={`${prefix}-initial-roles`}
            label="Initial approval roles"
            description=""
            hideHeader
            options={allRoles}
            counts={roleCounts}
            selected={draft.initialRoles}
            onChange={(next) => onChange({ initialRoles: next })}
            onAddRole={onAddRole}
            error={errors.initialRoles}
          />
        </EditorPanel>
        <EditorPanel
          step={5}
          title="Deployment approval"
          description="Tick who must approve before anything is deployed. They approve in the order you tick them."
          aside={<Tally count={draft.deploymentRoles.length} noun="approver" />}
        >
          <RolePicker
            id={`${prefix}-deployment-roles`}
            label="Deployment approval roles"
            description=""
            hideHeader
            options={allRoles}
            counts={roleCounts}
            selected={draft.deploymentRoles}
            onChange={(next) => onChange({ deploymentRoles: next })}
            onAddRole={onAddRole}
            error={errors.deploymentRoles}
          />
        </EditorPanel>
      </div>

      <EditorPanel
        step={6}
        title="Stream handoff"
        description="Turn on to create a task in each team's stream once the request is approved, and track it from the request."
        aside={(
          <Toggle
            id={`${prefix}-stream-enabled`}
            label="Stream handoff"
            checked={draft.linkStream}
            onChange={(linkStream) => onChange({
              linkStream,
              ...(linkStream && draft.handoffs.length === 0 ? { handoffs: [{ ...EMPTY_HANDOFF }] } : {}),
            })}
          />
        )}
      >
        {draft.linkStream ? (
          <DepartmentsEditor
            id={`${prefix}-stream`}
            handoffs={draft.handoffs}
            projects={projects.filter((project) => !isWorkspaceProject(project))}
            roles={allRoles}
            onChange={(handoffs) => onChange({ handoffs })}
          />
        ) : <p className="text-sm text-muted-foreground">Off. Approved requests stay in Service Requests.</p>}
      </EditorPanel>

      <div className="grid gap-5 md:grid-cols-2">
        <EditorPanel
          step={7}
          title="Readiness checks"
          description="Checklist the delivery team completes before asking for deployment approval."
          aside={<Tally count={draft.readiness.length} noun="check" />}
        >
          <InlineChecklist
            id={`${prefix}-readiness`}
            items={draft.readiness}
            onChange={(readiness) => onChange({ readiness })}
            itemLabel="Readiness check"
            placeholder="e.g. Hardware tested"
            error={errors.readiness}
          />
        </EditorPanel>
        <EditorPanel
          step={8}
          title="Validation checks"
          description="Checklist completed after deployment to confirm it was delivered as approved."
          aside={<Tally count={draft.validation.length} noun="check" />}
        >
          <InlineChecklist
            id={`${prefix}-validation`}
            items={draft.validation}
            onChange={(validation) => onChange({ validation })}
            itemLabel="Validation check"
            placeholder="e.g. Device online"
            error={errors.validation}
          />
        </EditorPanel>
      </div>

      <details className="group rounded-xl border border-input bg-card px-4 py-3 shadow-sm">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold">
          <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />
          Blocking rules
          {draft.rules.length ? <CountPill count={draft.rules.length} /> : <span className="text-xs font-normal text-muted-foreground">Optional</span>}
        </summary>
        <div className="mt-3">
          <RulesEditor
            id={`${prefix}-rules`}
            values={draft.rules}
            fieldOptions={[
              ...draft.sections.flatMap((section) => section.fields).map(fieldKeyOf).filter(Boolean),
            ].filter((field, index, rows) => rows.indexOf(field) === index)}
            onChange={(rules) => onChange({ rules })}
            error={errors.rules}
          />
        </div>
      </details>
    </div>
  );
}

export default function ServiceRequestSettings() {
  const { toast } = useOverlays();
  const { isOrgAdmin } = useSession();
  const [items, setItems] = useState<ServiceRequestType[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [roleData, setRoleData] = useState<ServiceRequestRolesResponse | null>(null);
  const [extraRoles, setExtraRoles] = useState<string[]>([]);
  const [removedRoles, setRemovedRoles] = useState<Set<string>>(() => new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState('types');
  const [createDraft, setCreateDraft] = useState<Draft | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState<ServiceRequestTemplateDto[]>([]);
  const [templateDialog, setTemplateDialog] = useState<{ type: ServiceRequestType; draft: Draft; name: string } | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const [projects, setProjects] = useState<ProjectDto[]>([]);

  const load = useCallback(async () => {
    try {
      const [next, roles, projectPage, savedTemplates] = await Promise.all([
        listServiceRequestTypes(true, true),
        getServiceRequestRoles(),
        apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
        listServiceRequestTemplates().catch(() => [] as ServiceRequestTemplateDto[]),
      ]);
      setItems(next);
      setTemplates(savedTemplates);
      setDrafts(Object.fromEntries(next.map((type) => [type.id, draftFor(type)])));
      setRoleData(roles);
      setProjects(projectPage.items.filter((project) => !project.archived_at));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to load catalog');
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  /** Roles a request type currently requires, saved or drafted; these stay off the removable list. */
  const rolesInUse = useMemo(() => new Set([
    ...items.flatMap((type) => [...type.initial_approval_roles, ...type.deployment_approval_roles]),
    ...Object.values(drafts).flatMap((draft) => [...draft.initialRoles, ...draft.deploymentRoles]),
  ]), [items, drafts]);

  const allRoles = useMemo(() => roleUniverse(
    roleData?.known_roles ?? [],
    (roleData?.items ?? []).map((row) => row.role),
    items.flatMap((type) => [...type.initial_approval_roles, ...type.deployment_approval_roles]),
    Object.values(drafts).flatMap((draft) => [...draft.initialRoles, ...draft.deploymentRoles]),
    extraRoles,
  ).filter((role) => !removedRoles.has(role)), [roleData, items, drafts, extraRoles, removedRoles]);

  const roleCounts = useMemo(
    () => Object.fromEntries((roleData?.items ?? []).map((row) => [row.role, row.approver_ids.length])),
    [roleData],
  );

  function update(id: string, patch: Partial<Draft>) {
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  }

  function addRole(role: string) {
    setExtraRoles((current) => (current.includes(role) ? current : [...current, role]));
    setRemovedRoles((current) => {
      if (!current.has(role)) return current;
      const next = new Set(current);
      next.delete(role);
      return next;
    });
  }

  /** Only offered once a role has no members and no request type needs it (enforced in the UI). */
  function removeRole(role: string) {
    setExtraRoles((current) => current.filter((value) => value !== role));
    setRemovedRoles((current) => new Set(current).add(role));
  }

  useEffect(() => {
    if (createDraft || (selectedId && items.some((type) => type.id === selectedId))) return;
    setSelectedId(items[0]?.id ?? null);
  }, [items, selectedId, createDraft]);

  function openCreate() {
    setCreateDraft({ ...BLANK_TYPE_DRAFT });
    setTemplateId('');
    setAttempted(false);
    setSelectedId(null);
  }

  /** Replaces the unsaved new type with a request type or a saved template, keeping a name already typed. */
  function startFrom(value: string) {
    const typedName = createDraft?.name ?? '';
    const projectIds = new Set(projects.map((project) => project.id));
    if (value.startsWith('type:')) {
      const type = items.find((row) => row.id === value.slice('type:'.length));
      if (type) {
        const copy: Draft = JSON.parse(JSON.stringify(draftFor(type)));
        setCreateDraft({ ...copy, name: typedName, active: false });
        setTemplateId(value);
        setAttempted(false);
        return;
      }
    }
    const templateIdValue = value.startsWith('template:') ? value.slice('template:'.length) : '';
    const template = templates.find((row) => row.id === templateIdValue);
    setCreateDraft(template
      ? { ...draftFromTemplate(template, projectIds), name: typedName }
      : { ...BLANK_TYPE_DRAFT, name: typedName });
    setTemplateId(template ? value : '');
    setAttempted(false);
  }

  async function saveTemplate() {
    if (!templateDialog) return;
    const name = templateDialog.name.trim();
    if (!name) return;
    const { name: _typeName, ...config } = payloadFor(templateDialog.type, templateDialog.draft);
    const replacing = templates.some((row) => row.name === name);
    setSavingTemplate(true);
    try {
      const saved = await saveServiceRequestTemplate({ name, source_type_id: templateDialog.type.id, config });
      setTemplates((current) => [...current.filter((row) => row.id !== saved.id && row.name !== saved.name), saved]
        .sort((left, right) => left.name.localeCompare(right.name)));
      setTemplateDialog(null);
      toast(replacing ? `Template “${name}” updated` : `Saved as template “${name}”`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  }

  async function removeType(type: ServiceRequestType) {
    if (!window.confirm(`Delete the request type “${type.name}”? This cannot be undone. A type that already has requests cannot be deleted.`)) return;
    setBusyId(type.id);
    try {
      await deleteServiceRequestType(type.id);
      setItems((current) => current.filter((row) => row.id !== type.id));
      setDrafts((current) => {
        const next = { ...current };
        delete next[type.id];
        return next;
      });
      setSelectedId(null);
      toast(`Deleted “${type.name}”`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete the type');
    } finally {
      setBusyId(null);
    }
  }

  async function removeTemplate(id: string) {
    const template = templates.find((row) => row.id === id);
    if (!template || !window.confirm(`Delete the template “${template.name}”? Types already created from it stay as they are.`)) return;
    try {
      await deleteServiceRequestTemplate(id);
      setTemplates((current) => current.filter((row) => row.id !== id));
      if (templateId === `template:${id}`) setTemplateId('');
      toast(`Template “${template.name}” deleted`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete template');
    }
  }

  function selectType(id: string) {
    setCreateDraft(null);
    setAttempted(false);
    setSelectedId(id);
  }

  function payloadFor(type: ServiceRequestType | null, draft: Draft) {
    const config = configPatchFor(type, draft);
    return {
      name: draft.name.trim(),
      description: draft.description,
      department: draft.department.trim() || null,
      sla_days: draft.sla ? Number(draft.sla) : null,
      field_schema: config.field_schema,
      rules: config.rules,
      stage_config: config.stage_config,
      initial_approval_roles: normalizeRoles(draft.initialRoles),
      deployment_approval_roles: normalizeRoles(draft.deploymentRoles),
      submission_requirements: {
        fields: config.submissionFields,
        type_data_fields: config.typeDataFields,
        require_attachments: draft.submissionRequireAttachments,
      },
      required_attachments: draft.requiredAttachments.map((label) => label.trim()).filter(Boolean),
      required_attachment_guidance: Object.fromEntries(
        draft.requiredAttachments
          .filter((label) => Boolean(label.trim()))
          .map((label) => [label.trim(), draft.requiredAttachmentGuidance[label]?.trim() ?? '']),
      ),
      readiness_template: draft.readiness.map((item) => ({ key: item.key.trim(), label: item.label.trim(), required: item.required === true })),
      validation_template: draft.validation.map((item) => ({ key: item.key.trim(), label: item.label.trim(), required: item.required === true })),
    };
  }

  /** Active publishes the type; inactive keeps it saved but hidden from requestors. */
  async function saveConfiguration(type: ServiceRequestType | null, draft: Draft) {
    setAttempted(true);
    if (Object.keys(editorErrors(draft)).length > 0) return;
    setBusyId(type?.id ?? 'new');
    try {
      const saved = type
        ? await apiFetch<ServiceRequestType>(v1Path(`/service-request-types/${type.id}`), {
          method: 'PATCH',
          body: JSON.stringify({ ...payloadFor(type, draft), archived: type.status === 'draft' ? false : !draft.active }),
        })
        : await apiFetch<ServiceRequestType>(v1Path('/service-request-types'), {
          method: 'POST',
          body: JSON.stringify(payloadFor(null, draft)),
        });
      if (draft.active && saved.status === 'draft') {
        await apiFetch(v1Path(`/service-request-types/${saved.id}/publish`), { method: 'POST' });
      }
      setCreateDraft(null);
      setAttempted(false);
      await load();
      setSelectedId(saved.id);
      toast(draft.active ? `${saved.name} is live` : `${saved.name} saved`);
    } catch (err) {
      await load();
      toast(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <PageFrame>
      <div className="max-w-6xl space-y-5">
        <PageTitle title="Service Request configuration" />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto w-full justify-start rounded-none border-b border-border bg-transparent p-0">
            <TabsTrigger value="types" className="rounded-none border-b-2 border-transparent px-3 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">Request types<Badge variant="secondary" className="ml-1.5 px-1.5">{items.length}</Badge></TabsTrigger>
            <TabsTrigger value="roles" className="rounded-none border-b-2 border-transparent px-3 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">Approval roles<Badge variant="secondary" className="ml-1.5 px-1.5">{allRoles.length}</Badge></TabsTrigger>
            <TabsTrigger value="general" className="rounded-none border-b-2 border-transparent px-3 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">General</TabsTrigger>
          </TabsList>

          <TabsContent value="types" forceMount className="data-[state=inactive]:hidden">
            <div className="grid items-start gap-5 lg:grid-cols-[17rem_minmax(0,1fr)]">
              <aside className="space-y-2 lg:sticky lg:top-4">
                <Button className="w-full" disabled={Boolean(createDraft)} onClick={openCreate}>
                  <Plus className="h-4 w-4" aria-hidden /> New type
                </Button>
                <nav aria-label="Request types" className="space-y-1">
                  {createDraft ? (
                    <div className="rounded-xl border-2 border-primary bg-card px-3 py-2.5 shadow-sm">
                      <span className="block truncate text-sm font-semibold">{createDraft.name.trim() || 'Untitled type'}</span>
                      <span className="text-xs text-muted-foreground">Unsaved</span>
                    </div>
                  ) : null}
                  {items.map((type) => {
                    const draft = drafts[type.id] ?? draftFor(type);
                    const selected = !createDraft && selectedId === type.id;
                    const live = type.status !== 'draft' && !type.archived_at;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        aria-current={selected ? 'true' : undefined}
                        onClick={() => selectType(type.id)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-xl border bg-card px-3 py-2.5 text-left transition hover:border-input hover:shadow-sm',
                          selected ? 'border-2 border-primary shadow-sm' : 'border-border',
                        )}
                      >
                        <span className={cn('h-2 w-2 shrink-0 rounded-full', live ? 'bg-emerald-500' : 'bg-muted-foreground/30')} aria-label={live ? 'Live' : 'Off'} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">{type.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {[draft.department, draft.sla ? `${draft.sla}d SLA` : null].filter(Boolean).join(' · ') || 'Not set up'}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </aside>

              {(() => {
                const type = createDraft ? null : items.find((row) => row.id === selectedId) ?? null;
                const draft = createDraft ?? (type ? drafts[type.id] ?? draftFor(type) : null);
                if (!draft) {
                  return (
                    <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-border bg-card px-6 py-20 text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Settings2 className="h-6 w-6" aria-hidden />
                      </div>
                      <div>
                        <p className="text-base font-semibold">No request types yet</p>
                        <p className="text-sm text-muted-foreground">Create one to get started.</p>
                      </div>
                      <Button onClick={openCreate}><Plus className="h-4 w-4" aria-hidden /> New type</Button>
                    </div>
                  );
                }
                const onChange = (patch: Partial<Draft>) => {
                  if (createDraft) setCreateDraft((current) => (current ? { ...current, ...patch } : current));
                  else if (type) update(type.id, patch);
                };
                const errors = attempted ? editorErrors(draft) : {};
                const unsaved = createDraft ? true : type ? JSON.stringify(draft) !== JSON.stringify(draftFor(type)) : false;
                const busy = busyId === (type?.id ?? 'new');
                return (
                  <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                    <div className="p-5 sm:p-6">
                      {createDraft ? (
                        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 px-4 py-3 text-sm text-blue-900 dark:text-blue-200">
                          <Copy className="h-4 w-4 shrink-0" aria-hidden />
                          <label htmlFor="sr-new-type-template" className="font-medium">Start from</label>
                          <select
                            id="sr-new-type-template"
                            className={cn(SELECT_CLASS, 'h-8 min-w-[14rem] bg-card')}
                            value={templateId}
                            onChange={(event) => startFrom(event.target.value)}
                          >
                            <option value="">Blank type</option>
                            {items.length ? (
                              <optgroup label="Request types">
                                {[...items].sort((left, right) => left.name.localeCompare(right.name)).map((row) => (
                                  <option key={row.id} value={`type:${row.id}`}>{row.name}</option>
                                ))}
                              </optgroup>
                            ) : null}
                            {templates.length ? (
                              <optgroup label="Saved templates">
                                {templates.map((row) => <option key={row.id} value={`template:${row.id}`}>{row.name}</option>)}
                              </optgroup>
                            ) : null}
                          </select>
                          {items.length === 0 && templates.length === 0 ? (
                            <span className="text-xs text-blue-800 dark:text-blue-300">Save a type as a template and it shows up here.</span>
                          ) : null}
                          {templateId.startsWith('type:') ? (
                            <span className="text-xs text-blue-800 dark:text-blue-300">Edit anything below. The original type stays as it is.</span>
                          ) : null}
                          {templateId.startsWith('template:') ? (
                            <>
                              <span className="text-xs text-blue-800 dark:text-blue-300">Edit anything below. The template stays as it is.</span>
                              <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 text-xs text-muted-foreground" onClick={() => void removeTemplate(templateId.slice('template:'.length))}>
                                <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete template
                              </Button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                      <TypeEditor
                        prefix={type ? `sr-type-${type.id}` : 'sr-new-type'}
                        draft={draft}
                        errors={errors}
                        onChange={onChange}
                        allRoles={allRoles}
                        roleCounts={roleCounts}
                        onAddRole={addRole}
                        projects={projects}
                      />
                    </div>
                    <div className="sticky bottom-0 flex items-center gap-2 border-t bg-card/95 px-5 py-3 backdrop-blur sm:px-6">
                      {!createDraft && type ? (
                        <Button
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          disabled={busy}
                          onClick={() => void removeType(type)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden /> Delete type
                        </Button>
                      ) : null}
                      {Object.keys(errors).length > 0
                        ? <span className="text-xs font-medium text-destructive">Fix the highlighted fields</span>
                        : unsaved && !createDraft ? <span className="text-xs text-muted-foreground">Unsaved changes</span> : null}
                      <div className="ml-auto flex gap-2">
                        {createDraft ? (
                          <Button variant="ghost" disabled={busy} onClick={() => { setCreateDraft(null); setAttempted(false); }}>Cancel</Button>
                        ) : type ? (
                          <>
                            <Button
                              variant="outline"
                              disabled={busy}
                              onClick={() => setTemplateDialog({ type, draft, name: draft.name.trim() || type.name })}
                            >
                              <Copy className="h-4 w-4" aria-hidden /> Save template
                            </Button>
                            <Button variant="ghost" disabled={busy || !unsaved} onClick={() => { setDrafts((current) => ({ ...current, [type.id]: draftFor(type) })); setAttempted(false); }}>Discard</Button>
                          </>
                        ) : null}
                        <Button disabled={busy || !unsaved} onClick={() => void saveConfiguration(type, draft)}>
                          {busy ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent value="roles" forceMount className="data-[state=inactive]:hidden">
            <ApprovalRoles
              allRoles={allRoles}
              rolesInUse={rolesInUse}
              data={roleData}
              onSaved={setRoleData}
              onAddRole={addRole}
              onRemoveRole={removeRole}
            />
          </TabsContent>

          <TabsContent value="general" forceMount className="space-y-4 data-[state=inactive]:hidden">
            {isOrgAdmin ? <ConfigChangeLog /> : null}
          </TabsContent>
        </Tabs>
      </div>
      <Dialog open={templateDialog !== null} onOpenChange={(open) => { if (!open) setTemplateDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save as template</DialogTitle>
            <DialogDescription>
              Saves this type’s sections, questions, documents, approvals, handoffs and checks. Pick it under “Start from” when you create a new type.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-2"
            onSubmit={(event) => { event.preventDefault(); void saveTemplate(); }}
          >
            <Label htmlFor="sr-template-name">Template name</Label>
            <Input
              id="sr-template-name"
              autoFocus
              value={templateDialog?.name ?? ''}
              onChange={(event) => setTemplateDialog((current) => (current ? { ...current, name: event.target.value } : current))}
            />
            {templateDialog && templates.some((row) => row.name === templateDialog.name.trim())
              ? <p className="text-xs text-amber-700 dark:text-amber-300">A template with this name exists. Saving replaces it.</p>
              : null}
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setTemplateDialog(null)}>Cancel</Button>
              <Button type="submit" disabled={savingTemplate || !templateDialog?.name.trim()}>
                {savingTemplate ? 'Saving…' : 'Save template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageFrame>
  );
}
